import { InteractionEvent, Sprite, Texture } from 'pixi.js';
import { Signal } from 'typed-signals';

import Curve from '../utils/Curve';
import { Maths } from '../utils/Maths';

export interface SwipeData
{
    velocityX: number,
    velocityY: number,
}

export class GestureArea extends Sprite
{
    public pressing = false;
    public dragging = false;
    public pointerPressX = 0;
    public pointerPressY = 0;
    public pointerX = 0;
    public pointerY = 0;
    public pointerVelocityX = 0;
    public pointerVelocityY = 0;
    public posX = 0;
    public roundedPosX = 0;

    public changed = false;
    public snapped = false;

    private snapStart = 0;
    private snapEnd = 0;
    private snapDuration = 0;
    private snapTime = 0;

    public onSwipe: Signal<(area: GestureArea, swipe: SwipeData) => void>;
    public onStartDrag: Signal<(area: GestureArea) => void>;
    public onStopDrag: Signal<(area: GestureArea) => void>;
    public onSnap: Signal<(area: GestureArea) => void>;
    public onChange: Signal<(area: GestureArea) => void>;

    constructor()
    {
        super(Texture.WHITE);

        this.onSwipe = new Signal();
        this.onStartDrag = new Signal();
        this.onStopDrag = new Signal();
        this.onSnap = new Signal();
        this.onChange = new Signal();

        this.interactive = true;
        this.alpha = 0;
        this.posX = 0;
        this.roundedPosX = 0;
        this.on('pointerdown', this.onPointerDown.bind(this));
        this.on('pointermove', this.onPointerMove.bind(this));
        this.on('pointerup', this.onPointerUp.bind(this));
        this.on('pointerupoutside', this.onPointerUp.bind(this));
        this.on('pointercancel', this.onPointerUp.bind(this));
    }

    public setPosX(v: number): void
    {
        this.posX = v;
        this.roundedPosX = Math.round(v);
    }

    public update(): void
    {
        this.snapped = false;
        this.changed = false;

        const delta = 1;

        if (!this.pressing && this.snapDuration && this.snapTime < this.snapDuration)
        {
            this.snapTime += delta * (1 / 60);

            if (this.snapTime >= this.snapDuration)
            {
                this.snapTime = 0;
                this.snapDuration = 0;
                this.posX = this.snapEnd;
                this.snapped = true;
                this.onSnap.emit(this);
            }
            else
            {
                this.posX = Maths.tween(this.snapStart, this.snapEnd, Curve.backOut(this.snapTime / this.snapDuration));
            }
        }
        else if (this.pressing)
        {
            this.posX -= this.pointerVelocityX * 0.005;
        }

        const roundedPosX = Math.round(this.posX);

        if (this.roundedPosX !== roundedPosX)
        {
            this.roundedPosX = roundedPosX;
            this.changed = true;
            this.onChange.emit(this);
        }

        this.pointerVelocityX = 0;
    }

    private onPointerDown(e: InteractionEvent): void
    {
        this.pressing = true;
        this.dragging = false;
        this.pointerX = e.data.global.x;
        this.pointerY = e.data.global.x;
        this.pointerPressX = e.data.global.x;
        this.pointerPressY = e.data.global.x;
    }

    private onPointerMove(e: InteractionEvent): void
    {
        if (!this.pressing) return;
        const point = e.data.global;

        this.pointerVelocityX = point.x - this.pointerX;
        this.pointerVelocityY = point.y - this.pointerY;
        this.pointerX = point.x;
        this.pointerY = point.y;

        if (!this.dragging)
        {
            this.dragging = true;
            this.onStartDrag.emit(this);
        }
    }

    private onPointerUp(): void
    {
        if (!this.pressing) return;
        this.pressing = false;

        const dx = this.pointerVelocityX;
        const dy = this.pointerVelocityY;
        const distance = Math.sqrt((dx * dx) + (dy * dy));

        if (distance >= 1)
        {
            this.onSwipe.emit(this, { velocityX: dx, velocityY: dy });
        }

        if (this.dragging)
        {
            this.dragging = false;
            this.onStopDrag.emit(this);
        }

        this.tween(-this.pointerVelocityX * 0.07);

        this.pointerVelocityX = 0;
        this.pointerVelocityY = 0;
    }

    public next(): void
    {
        this.tween(1, 0.3);
    }

    public prev(): void
    {
        this.tween(-1, 0.3);
    }

    private tween(diff: number, speed = 1): void
    {
        this.snapStart = this.posX;
        this.snapEnd = Math.round(this.posX + diff);
        this.snapDuration = Maths.clamp(Math.abs(this.snapEnd - this.snapStart) * speed, 0.2, 1.2);
        this.snapTime = 0;
    }
}
